import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Shield, BarChart3, Settings } from 'lucide-react';

const CookiePolicy = () => {
  const { language } = useLanguage();

  const content = {
    pt: {
      title: 'Política de Cookies',
      lastUpdate: 'Última atualização: Janeiro de 2025',
      backHome: 'Voltar à página inicial',
      intro: 'Esta Política de Cookies explica como a Contabilidade e Fiscalidade, Lda utiliza cookies e tecnologias semelhantes no seu website, em conformidade com a legislação europeia aplicável.',
      sections: [
        {
          icon: Cookie,
          title: '1. O que são Cookies?',
          content: 'Cookies são pequenos ficheiros de texto que são armazenados no seu dispositivo (computador, tablet ou smartphone) quando visita um website. Os cookies permitem que o website reconheça o seu dispositivo e memorize informações sobre as suas preferências ou ações passadas.'
        },
        {
          icon: Shield,
          title: '2. Cookies que Utilizamos',
          content: 'O nosso website foi desenvolvido com uma abordagem de privacidade desde a conceção (privacy by design). Atualmente:',
          list: [
            'NÃO utilizamos cookies de marketing ou publicidade',
            'NÃO utilizamos cookies de rastreamento de terceiros',
            'NÃO vendemos nem partilhamos dados de navegação com terceiros',
            'NÃO utilizamos cookies para criar perfis de utilizadores'
          ]
        },
        {
          icon: Settings,
          title: '3. Cookies Técnicos Essenciais',
          content: 'Poderemos utilizar apenas cookies estritamente necessários para o funcionamento do website, tais como:',
          list: [
            'Cookies de sessão: temporários, eliminados quando fecha o navegador, essenciais para a navegação',
            'Cookies de preferência de idioma: para memorizar a sua escolha entre Português e Inglês'
          ],
          extra: 'Estes cookies não recolhem informações que possam ser utilizadas para fins de marketing ou para identificar pessoalmente o utilizador.'
        },
        {
          icon: BarChart3,
          title: '4. Análise de Tráfego (Analytics)',
          content: 'Caso venha a ser implementada análise de tráfego no website, garantimos que:',
          list: [
            'Será utilizada uma solução que respeite a privacidade do utilizador',
            'Os dados serão agregados e anonimizados',
            'Não serão utilizados cookies de rastreamento',
            'Não será possível identificar utilizadores individuais',
            'Os dados serão utilizados apenas para melhorar a experiência do website'
          ],
          extra: 'Comprometemo-nos a atualizar esta política caso sejam implementadas ferramentas de análise.'
        },
        {
          icon: Settings,
          title: '5. Como Gerir os Cookies',
          content: 'Pode controlar e/ou eliminar cookies através das definições do seu navegador. A maioria dos navegadores permite:',
          list: [
            'Ver os cookies armazenados e eliminá-los individualmente',
            'Bloquear cookies de terceiros',
            'Bloquear cookies de determinados websites',
            'Bloquear todos os cookies',
            'Eliminar todos os cookies quando fechar o navegador'
          ],
          extra: 'Note que, se optar por bloquear todos os cookies, algumas funcionalidades do website poderão não funcionar corretamente.'
        },
        {
          icon: Shield,
          title: '6. Mais Informações',
          content: 'Para mais informações sobre a utilização de cookies e proteção de dados, consulte a nossa Política de Privacidade ou contacte-nos através de:',
          contact: {
            email: 'privacidade@cflda.pt',
            phone: '911 000 000'
          }
        }
      ]
    },
    en: {
      title: 'Cookie Policy',
      lastUpdate: 'Last updated: January 2025',
      backHome: 'Back to homepage',
      intro: 'This Cookie Policy explains how Contabilidade e Fiscalidade, Lda uses cookies and similar technologies on its website, in compliance with applicable European legislation.',
      sections: [
        {
          icon: Cookie,
          title: '1. What are Cookies?',
          content: 'Cookies are small text files that are stored on your device (computer, tablet or smartphone) when you visit a website. Cookies allow the website to recognize your device and remember information about your preferences or past actions.'
        },
        {
          icon: Shield,
          title: '2. Cookies We Use',
          content: 'Our website was developed with a privacy by design approach. Currently:',
          list: [
            'We do NOT use marketing or advertising cookies',
            'We do NOT use third-party tracking cookies',
            'We do NOT sell or share browsing data with third parties',
            'We do NOT use cookies to create user profiles'
          ]
        },
        {
          icon: Settings,
          title: '3. Essential Technical Cookies',
          content: 'We may only use cookies strictly necessary for the website to function, such as:',
          list: [
            'Session cookies: temporary, deleted when you close the browser, essential for navigation',
            'Language preference cookies: to remember your choice between Portuguese and English'
          ],
          extra: 'These cookies do not collect information that can be used for marketing purposes or to personally identify the user.'
        },
        {
          icon: BarChart3,
          title: '4. Traffic Analysis (Analytics)',
          content: 'If traffic analysis is implemented on the website, we guarantee that:',
          list: [
            'A privacy-respecting solution will be used',
            'Data will be aggregated and anonymized',
            'No tracking cookies will be used',
            'Individual users cannot be identified',
            'Data will only be used to improve the website experience'
          ],
          extra: 'We commit to updating this policy if analytics tools are implemented.'
        },
        {
          icon: Settings,
          title: '5. How to Manage Cookies',
          content: 'You can control and/or delete cookies through your browser settings. Most browsers allow you to:',
          list: [
            'View stored cookies and delete them individually',
            'Block third-party cookies',
            'Block cookies from specific websites',
            'Block all cookies',
            'Delete all cookies when you close the browser'
          ],
          extra: 'Note that if you choose to block all cookies, some website features may not work properly.'
        },
        {
          icon: Shield,
          title: '6. More Information',
          content: 'For more information about the use of cookies and data protection, please see our Privacy Policy or contact us at:',
          contact: {
            email: 'privacidade@cflda.pt',
            phone: '911 000 000'
          }
        }
      ]
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backHome}
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">{t.title}</h1>
          <p className="text-slate-500 mt-2">{t.lastUpdate}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100 mb-8">
          <p className="text-slate-600 leading-relaxed">{t.intro}</p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {t.sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-teal-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 pt-1">{section.title}</h2>
                </div>
                
                <p className="text-slate-600 mb-4 leading-relaxed">{section.content}</p>
                
                {section.list && (
                  <ul className="space-y-2 mb-4">
                    {section.list.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600">
                        <span className="w-1.5 h-1.5 bg-teal-500 rounded-full mt-2 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                
                {section.extra && (
                  <p className="text-slate-600 bg-slate-50 p-4 rounded-lg">{section.extra}</p>
                )}
                
                {section.contact && (
                  <div className="bg-teal-50 p-4 rounded-lg mt-4">
                    {section.contact.email && (
                      <p className="text-teal-600">
                        <a href={`mailto:${section.contact.email}`} className="hover:underline">
                          {section.contact.email}
                        </a>
                      </p>
                    )}
                    {section.contact.phone && (
                      <p className="text-slate-600">{section.contact.phone}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Link to Privacy Policy */}
        <div className="mt-8 text-center">
          <Link 
            to="/politica-privacidade" 
            className="text-teal-600 hover:text-teal-700 hover:underline transition-colors"
          >
            {language === 'pt' ? 'Consultar Política de Privacidade' : 'View Privacy Policy'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
